import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DynamicObjectInterface } from '../../../../sdk/type';
import {
  Button,
  ButtonIconPosition,
  ButtonSize,
  ButtonStyles,
  ButtonTypes,
  Card,
  DropDown,
  DropDownDataInterface,
  DropDownValidations,
  FAIcon,
  IconStyle,
  Input,
  InputTypes,
  InputValidations,
  MLPVersion,
  Modal,
  TextAlign,
  Theme,
} from '../../../../ui/components';
import styles from '../../teams/edit/participant-details/index.module.scss';
import { CampaignData } from './[recId]';

export interface DetailsModalProps {
  onSave?: DynamicObjectInterface;
  onCancel?: DynamicObjectInterface;
  campaignData: CampaignData | null;
  handleOnChange?: (value: DynamicObjectInterface, key: string, ...paths: string[]) => void;
  formData?: CampaignData | null;
}

export const indicatorWinningCriteriaList = [
  { value: '', title: '' },
  { value: 'gain', title: '+' },
  { value: 'loss', title: '-' },
];

export const indicatorPrerequisiteCriteriaList = [
  { value: '', title: '' },
  { value: 'above', title: 'Above' },
  { value: 'below', title: 'Below' },
];

export const ruleOperatorList = [
  { value: 'AND', title: 'AND' },
  { value: 'OR', title: 'OR' },
];

export const ruleTypeList = [
  { value: 'BUNDLE', title: 'BUNDLE' },
  { value: 'BASE', title: 'BASE' },
];

export const WinningCriteriaModal = ({ onSave, onCancel, campaignData }: DetailsModalProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<CampaignData | null>(
    JSON.parse(JSON.stringify(campaignData)) as CampaignData,
  );
  const [showDelete, setShowDelete] = useState(-1);

  const handleOnChange = (value: DynamicObjectInterface, path: string) => {
    setFormData(prev => {
      if (!prev) return prev;
      const paths = path.split('.');
      const newData = JSON.parse(JSON.stringify(prev));
      let current = newData;

      for (let i = 0; i < paths.length - 1; i++) {
        const key = paths[i];
        current[key] = Array.isArray(current[key]) ? [...current[key]] : { ...current[key] };
        current = current[key];
      }

      const lastKey = paths[paths.length - 1];
      current[lastKey] = value;
      return newData;
    });
  };

  const handleAdd = () => {
    setFormData(prev => {
      if (!prev) return prev;
      const newData = JSON.parse(JSON.stringify(prev));

      // Ensure the rules array structure exists
      if (!newData.activities.participant.rules) {
        newData.activities.participant.rules = [];
      }
      if (!newData.activities.participant.rules[4]) {
        newData.activities.participant.rules[4] = { subKpiCode: [] };
      }

      newData.activities.participant.rules[4].subKpiCode.push({
        subKpiCode: `TEMP-${Date.now()}`,
      });

      return newData;
    });
  };

  const handleDeleteButton = (index: number) => {
    setShowDelete(index);
  };

  const handleDelete = (index: number) => {
    setFormData(prev => {
      if (!prev) return prev;
      const newData = JSON.parse(JSON.stringify(prev));

      if (newData.activities.participant.rules?.[4]?.subKpiCode) {
        newData.activities.participant.rules[4].subKpiCode.splice(index, 1);
      }

      return newData;
    });
    setShowDelete(-1);
  };

  const deletePrompt = (
    <>
      {showDelete !== -1 && (
        <Modal
          primaryButtonLabel={''}
          headline={`${t('admin.campaignConfig.deleteIndicator')}`}
          closeIcon={false}
          containerWidth={'460px'}
          primaryButton={
            <Button
              title={t('admin.rewardMaster.confirm')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                handleDelete(showDelete);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={
            <Button
              title={t('admin.rewardMaster.cancel')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                setShowDelete(-1);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              theme={Theme.BLACK}
              data-testid="test-cancel-button-popup-delete-modal"
            />
          }
        />
      )}
    </>
  );
  return (
    <Modal
      primaryButtonLabel={''}
      headline={t('admin.campaignConfig.editWinningPercentage')}
      textAlign={TextAlign.LEFT}
      closeIcon={false}
      containerWidth={'1200px'}
      primaryButton={
        <Button
          title={t('admin.participant.save')}
          type={ButtonTypes.BUTTON}
          onClick={() => onSave(formData)}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.SOLID}
          data-testid="test-add-button-popup"
        />
      }
      secondaryButton={
        <Button
          title={t('admin.participant.cancel')}
          type={ButtonTypes.BUTTON}
          onClick={onCancel}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.OUTLINE}
          theme={Theme.BLACK}
          data-testid="test-cancel-button-popup-delete-modal"
        />
      }
    >
      <div
        className={`${styles['modal-reward']} ${
          formData?.activities?.participant?.rules?.[4]?.subKpiCode &&
          formData?.activities?.participant?.rules?.[4]?.subKpiCode?.length > 0
            ? styles['increased-height']
            : ''
        }`}
      >
        {formData?.activities?.participant?.rules?.[4]?.subKpiCode?.map((item, index) => (
          <Card key={item.subKpiCode}>
            <div className={styles['modal-reward-headline']}>
              <div>
                {t('admin.campaignConfig.indicatorLabel')} {index + 1}
              </div>

              <FAIcon
                iconName={'trash-can'}
                onClick={e => {
                  handleDeleteButton(index);
                  e.stopPropagation();
                }}
                theme={Theme.RED}
                title={'Delete'}
                className={styles['modal-reward-delete']}
              />
            </div>
            <div className={styles['modal-details-form']}>
              <Input
                name={'indicatorName'}
                value={item.indicatorName}
                mlpVersion={MLPVersion.ONE}
                label={t('admin.campaignConfig.indicatorName')}
                onChange={e =>
                  handleOnChange(e.target.value, `activities.participant.rules.4.subKpiCode.${index}.indicatorName`)
                }
                required={true}
              />
              <DropDown
                name={'indicatorWinningCriteria'}
                dropdownLabel={t('admin.campaignConfig.indicatorWinningCriteria')}
                placeholder={t('admin.campaignConfig.indicatorWinningCriteria')}
                value={indicatorWinningCriteriaList.find(i => i.value === item.indicatorWinningCriteria)?.title}
                mlpVersion={MLPVersion.ONE}
                data={indicatorWinningCriteriaList}
                configData={{
                  label: 'title',
                  onSelect: (selectedOption: DropDownDataInterface) => {
                    handleOnChange(
                      selectedOption.value,
                      `activities.participant.rules.4.subKpiCode.${index}.indicatorWinningCriteria`,
                    );
                  },
                }}
                required={true}
              />
              <Input
                name={'indicatorValue'}
                type={InputTypes.NUMBER}
                value={item.targetFrom ?? ''}
                mlpVersion={MLPVersion.ONE}
                label={t('admin.campaignConfig.indicatorValue')}
                onChange={e =>
                  handleOnChange(
                    e.target.value.toString(),
                    `activities.participant.rules.4.subKpiCode.${index}.targetFrom`,
                  )
                }
                required={true}
              />
              <Input
                name={'indicatorMin'}
                type={InputTypes.NUMBER}
                value={item.indicatorMin ?? ''}
                mlpVersion={MLPVersion.ONE}
                label={t('admin.campaignConfig.indicatorMin')}
                onChange={e =>
                  handleOnChange(
                    e.target.value.toString(),
                    `activities.participant.rules.4.subKpiCode.${index}.indicatorMin`,
                  )
                }
              />
              <Input
                name={'indicatorMax'}
                type={InputTypes.NUMBER}
                value={item.indicatorMax ?? ''}
                mlpVersion={MLPVersion.ONE}
                label={t('admin.campaignConfig.indicatorMax')}
                onChange={e =>
                  handleOnChange(
                    e.target.value.toString(),
                    `activities.participant.rules.4.subKpiCode.${index}.indicatorMax`,
                  )
                }
              />
              <Input
                name={'indicatorMeasurementUnit'}
                value={item.indicatorMeasurementUnit}
                mlpVersion={MLPVersion.ONE}
                label={t('admin.campaignConfig.indicatorMeasurementUnit')}
                onChange={e =>
                  handleOnChange(
                    e.target.value,
                    `activities.participant.rules.4.subKpiCode.${index}.indicatorMeasurementUnit`,
                  )
                }
                required={true}
              />
              <Input
                name={'indicatorPlaceholder'}
                value={item.indicatorPlaceholder}
                mlpVersion={MLPVersion.ONE}
                label={t('admin.campaignConfig.indicatorPlaceholder')}
                onChange={e =>
                  handleOnChange(
                    e.target.value,
                    `activities.participant.rules.4.subKpiCode.${index}.indicatorPlaceholder`,
                  )
                }
              />

              <DropDown
                name={'indicatorPrerequisiteCriteria'}
                dropdownLabel={t('admin.campaignConfig.indicatorPrerequisiteCriteria')}
                placeholder={t('admin.campaignConfig.indicatorPrerequisiteCriteria')}
                value={
                  indicatorPrerequisiteCriteriaList.find(i => i.value === item.indicatorPrerequisiteCriteria)?.title
                }
                mlpVersion={MLPVersion.ONE}
                data={indicatorPrerequisiteCriteriaList}
                configData={{
                  label: 'title',
                  onSelect: (selectedOption: DropDownDataInterface) => {
                    handleOnChange(
                      selectedOption.value,
                      `activities.participant.rules.4.subKpiCode.${index}.indicatorPrerequisiteCriteria`,
                    );
                  },
                }}
              />

              <Input
                name={'indicatorPrerequisite'}
                type={InputTypes.NUMBER}
                value={item.indicatorPrerequisite ?? ''}
                mlpVersion={MLPVersion.ONE}
                label={t('admin.campaignConfig.indicatorPrerequisite')}
                onChange={e =>
                  handleOnChange(
                    e.target.value.toString(),
                    `activities.participant.rules.4.subKpiCode.${index}.indicatorPrerequisite`,
                  )
                }
              />

              <div className={styles['checkbox-container']} key={`checkbox-${item.subKpiCode}-percentage`}>
                <input
                  id={`checkbox-${item.subKpiCode}-percentage`}
                  className={styles['checkbox-container-checkbox']}
                  type="checkbox"
                  checked={item.percentageCalculation === 1}
                  onChange={() =>
                    handleOnChange(
                      item.percentageCalculation ? 0 : 1,
                      `activities.participant.rules.4.subKpiCode.${index}.percentageCalculation`,
                    )
                  }
                />
                <label
                  className={styles['checkbox-container-label-bold']}
                  htmlFor={`checkbox-${item.subKpiCode}-percentage`}
                >
                  Use Percentage Calculation
                </label>
              </div>

              <div className={styles['checkbox-container']} key={`checkbox-${item.subKpiCode}-progress`}>
                <input
                  id={`checkbox-${item.subKpiCode}-progress`}
                  className={styles['checkbox-container-checkbox']}
                  type="checkbox"
                  checked={item.displayInProgressMonitoring === 'on'}
                  onChange={() =>
                    handleOnChange(
                      item.displayInProgressMonitoring ? '' : 'on',
                      `activities.participant.rules.4.subKpiCode.${index}.displayInProgressMonitoring`,
                    )
                  }
                />
                <label
                  className={styles['checkbox-container-label-bold']}
                  htmlFor={`checkbox-${item.subKpiCode}-progress`}
                >
                  Display in Progress Monitoring Section
                </label>
              </div>

              <div className={styles['checkbox-container']} key={`checkbox-${item.subKpiCode}-preAssessment`}>
                <input
                  id={`checkbox-${item.subKpiCode}-preAssessment`}
                  className={styles['checkbox-container-checkbox']}
                  type="checkbox"
                  checked={item.disablePreAssessment === 1}
                  onChange={() =>
                    handleOnChange(
                      item.disablePreAssessment ? 0 : 1,
                      `activities.participant.rules.4.subKpiCode.${index}.disablePreAssessment`,
                    )
                  }
                />
                <label
                  className={styles['checkbox-container-label-bold']}
                  htmlFor={`checkbox-${item.subKpiCode}-preAssessment`}
                >
                  Disable in Pre-Assessment Section
                </label>
              </div>
            </div>
            {deletePrompt}
          </Card>
        ))}
        <Button
          title={t('admin.campaignConfig.addWinningCriteria')}
          type={ButtonTypes.BUTTON}
          onClick={handleAdd}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.OUTLINE}
          data-testid="test-add-button-popup"
        />
      </div>
    </Modal>
  );
};

export const FileUploaderModal = ({ onSave, onCancel, campaignData }: DetailsModalProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<CampaignData | null>(
    JSON.parse(JSON.stringify(campaignData)) as CampaignData,
  );
  const [showDelete, setShowDelete] = useState(-1);

  const handleOnChange = (value: DynamicObjectInterface, path: string) => {
    setFormData(prev => {
      if (!prev) return prev;
      const paths = path.split('.');
      const newData = JSON.parse(JSON.stringify(prev));
      let current = newData;

      for (let i = 0; i < paths.length - 1; i++) {
        const key = paths[i];
        current[key] = Array.isArray(current[key]) ? [...current[key]] : { ...current[key] };
        current = current[key];
      }

      const lastKey = paths[paths.length - 1];
      current[lastKey] = value;
      return newData;
    });
  };

  const handleAdd = () => {
    setFormData(prev => {
      if (!prev) return prev;
      const newData = JSON.parse(JSON.stringify(prev));

      // Ensure the rules array structure exists
      if (!newData.activities?.participant?.fileUploader) {
        newData.activities.participant.fileUploader = [];
      }

      newData.activities.participant.fileUploader.push({
        fieldCode: `TEMP-${Date.now()}`,
      });

      return newData;
    });
  };

  const handleDeleteButton = (index: number) => {
    setShowDelete(index);
  };

  const handleDelete = (index: number) => {
    setFormData(prev => {
      if (!prev) return prev;
      const newData = JSON.parse(JSON.stringify(prev));

      if (newData.activities.participant.fileUploader) {
        newData.activities.participant.fileUploader.splice(index, 1);
      }

      return newData;
    });
    setShowDelete(-1);
  };

  const deletePrompt = (
    <>
      {showDelete !== -1 && (
        <Modal
          primaryButtonLabel={''}
          headline={`Are you sure you want to delete the file type? This action cannot be undone!`}
          closeIcon={false}
          containerWidth={'460px'}
          primaryButton={
            <Button
              title={t('admin.rewardMaster.confirm')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                handleDelete(showDelete);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={
            <Button
              title={t('admin.rewardMaster.cancel')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                setShowDelete(-1);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              theme={Theme.BLACK}
              data-testid="test-cancel-button-popup-delete-modal"
            />
          }
        />
      )}
    </>
  );
  return (
    <Modal
      primaryButtonLabel={''}
      headline={`Edit Assessment File Upload`}
      textAlign={TextAlign.LEFT}
      closeIcon={false}
      containerWidth={'1200px'}
      primaryButton={
        <Button
          title={t('admin.participant.save')}
          type={ButtonTypes.BUTTON}
          onClick={() => onSave(formData)}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.SOLID}
          data-testid="test-add-button-popup"
        />
      }
      secondaryButton={
        <Button
          title={t('admin.participant.cancel')}
          type={ButtonTypes.BUTTON}
          onClick={onCancel}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.OUTLINE}
          theme={Theme.BLACK}
          data-testid="test-cancel-button-popup-delete-modal"
        />
      }
    >
      <div className={`${styles['modal-reward']}`}>
        {formData?.activities?.participant?.fileUploader?.map((item, index) => (
          <Card key={item.fieldCode}>
            <div className={styles['modal-reward-headline']}>
              <div>File Type {index + 1}</div>

              <FAIcon
                iconName={'trash-can'}
                onClick={e => {
                  handleDeleteButton(index);
                  e.stopPropagation();
                }}
                theme={Theme.RED}
                title={'Delete'}
                className={styles['modal-reward-delete']}
              />
            </div>
            <div className={styles['modal-details-form']}>
              <Input
                name={'fieldName'}
                value={item.fieldName}
                mlpVersion={MLPVersion.ONE}
                label={'Field Name'}
                onChange={e => handleOnChange(e.target.value, `activities.participant.fileUploader.${index}.fieldName`)}
                required={true}
              />
              <Input
                name={'numberOfFile'}
                type={InputTypes.NUMBER}
                value={item.numberOfFile ?? ''}
                mlpVersion={MLPVersion.ONE}
                label={'Number (how many file uploader required)'}
                onChange={e =>
                  handleOnChange(e.target.value.toString(), `activities.participant.fileUploader.${index}.numberOfFile`)
                }
              />
            </div>
            {deletePrompt}
          </Card>
        ))}
        <Button
          title={'Add File Type'}
          type={ButtonTypes.BUTTON}
          onClick={handleAdd}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.OUTLINE}
          data-testid="test-add-button-popup"
        />
      </div>
    </Modal>
  );
};

export const OrderRuleModal = ({ onSave, onCancel, campaignData }: DetailsModalProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<CampaignData | null>(
    JSON.parse(JSON.stringify(campaignData)) as CampaignData,
  );
  const [showDelete, setShowDelete] = useState(false);
  const [deletedPosition, setDeletedPosition] = useState(-1);
  const [deletedGroupPosition, setDeletedGroupPosition] = useState(-1);

  const handleOnChange = (value: DynamicObjectInterface, path: string) => {
    setFormData(prev => {
      if (!prev) return prev;
      const paths = path.split('.');
      const newData = JSON.parse(JSON.stringify(prev));
      let current = newData;

      for (let i = 0; i < paths.length - 1; i++) {
        const key = paths[i];
        current[key] = Array.isArray(current[key]) ? [...current[key]] : { ...current[key] };
        current = current[key];
      }

      const lastKey = paths[paths.length - 1];
      current[lastKey] = value;
      return newData;
    });
  };

  const handleAdd = (groupIndex?: number, position?: number) => {
    setFormData(prev => {
      if (!prev) return prev;
      const newData = JSON.parse(JSON.stringify(prev));
      const allGroups = newData.activities?.order?.rules ?? [];

      // Ensure the targeted group exists
      const targetGroup = allGroups[groupIndex ?? 0] ?? [];

      const newRule = {
        type: '',
        operator: '',
        value: '',
      };

      const insertIndex = typeof position === 'number' && position >= 0 ? position : targetGroup.length;

      // Insert into the selected group
      targetGroup.splice(insertIndex + 1, 0, newRule);

      // Reassign updated group back into rules
      allGroups[groupIndex ?? 0] = targetGroup;
      newData.activities.order.rules = allGroups;

      return newData;
    });
  };

  const handleDeleteButton = (groupIndex: number, position?: number) => {
    setShowDelete(true);
    setDeletedGroupPosition(groupIndex);
    setDeletedPosition(position ?? -1);
  };

  const handleDelete = () => {
    setFormData(prev => {
      if (!prev) return prev;
      const newData = JSON.parse(JSON.stringify(prev));
      const allGroups = newData.activities?.order?.rules ?? [];

      // Guard against invalid indexes
      if (
        deletedGroupPosition >= 0 &&
        deletedGroupPosition < allGroups.length &&
        Array.isArray(allGroups[deletedGroupPosition])
      ) {
        if (
          deletedPosition !== -1 &&
          deletedPosition >= 0 &&
          deletedPosition < allGroups[deletedGroupPosition].length
        ) {
          // Delete specific rule in the group
          allGroups[deletedGroupPosition].splice(deletedPosition, 1);

          // Optionally remove the group if it becomes empty
          if (allGroups[deletedGroupPosition].length === 0) {
            allGroups.splice(deletedGroupPosition, 1);
          }
        } else {
          // Delete the entire group
          allGroups.splice(deletedGroupPosition, 1);
        }

        newData.activities.order.rules = allGroups;
      }

      return newData;
    });
    setShowDelete(false);
    setDeletedGroupPosition(0);
    setDeletedPosition(0);
  };

  const deletePrompt = (
    <>
      {showDelete && (
        <Modal
          primaryButtonLabel={''}
          headline={`${t('admin.campaignConfig.deleteRule')}`}
          closeIcon={false}
          containerWidth={'460px'}
          primaryButton={
            <Button
              title={t('admin.rewardMaster.confirm')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                handleDelete();
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={
            <Button
              title={t('admin.rewardMaster.cancel')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                setShowDelete(false);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              theme={Theme.BLACK}
              data-testid="test-cancel-button-popup-delete-modal"
            />
          }
        />
      )}
    </>
  );

  return (
    <Modal
      primaryButtonLabel={''}
      headline={t('admin.campaignConfig.editOrderRule')}
      textAlign={TextAlign.LEFT}
      closeIcon={false}
      containerWidth={'1200px'}
      primaryButton={
        <Button
          title={t('admin.participant.save')}
          type={ButtonTypes.BUTTON}
          onClick={() => onSave(formData)}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.SOLID}
          data-testid="test-add-button-popup"
        />
      }
      secondaryButton={
        <Button
          title={t('admin.participant.cancel')}
          type={ButtonTypes.BUTTON}
          onClick={onCancel}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.OUTLINE}
          theme={Theme.BLACK}
          data-testid="test-cancel-button-popup-delete-modal"
        />
      }
    >
      <div className={`${styles['modal-reward']}`}>
        {formData?.activities?.order?.rules.map((record, index) => (
          <Card key={`rule-${index + 1}`}>
            <div className={styles['modal-reward-headline']}>
              <div>
                {t('admin.campaignConfig.rule')} {index + 1}
              </div>
              <FAIcon
                iconName={'trash-can'}
                onClick={e => {
                  handleDeleteButton(index);
                  e.stopPropagation();
                }}
                theme={Theme.RED}
                title={'Delete'}
                className={styles['modal-reward-delete']}
              />
            </div>

            {deletePrompt}

            {Array.isArray(record) &&
              record.map((item, itemIndex) => (
                <div className={styles['modal-details-form-5']}>
                  {itemIndex > 0 ? (
                    <DropDown
                      name={'operator'}
                      dropdownLabel={t('admin.campaignConfig.operator')}
                      placeholder={t('admin.campaignConfig.operator')}
                      value={ruleOperatorList.find(i => i.value === item.operator)?.title}
                      mlpVersion={MLPVersion.ONE}
                      data={ruleOperatorList}
                      configData={{
                        label: 'title',
                        onSelect: (selectedOption: DropDownDataInterface) => {
                          handleOnChange(selectedOption.value, `activities.order.rules.${index}.${itemIndex}.operator`);
                        },
                      }}
                      required={true}
                      status={item.operator ? DropDownValidations.DEFAULT : DropDownValidations.INVALID}
                    />
                  ) : (
                    <div></div>
                  )}

                  <DropDown
                    name={'type'}
                    dropdownLabel={t('admin.campaignConfig.type')}
                    placeholder={t('admin.campaignConfig.type')}
                    value={ruleTypeList.find(i => i.value === item.type)?.title}
                    mlpVersion={MLPVersion.ONE}
                    data={ruleTypeList}
                    configData={{
                      label: 'title',
                      onSelect: (selectedOption: DropDownDataInterface) => {
                        handleOnChange(selectedOption.value, `activities.order.rules.${index}.${itemIndex}.type`);
                      },
                    }}
                    required={true}
                    status={item.type ? DropDownValidations.DEFAULT : DropDownValidations.INVALID}
                  />

                  <Input
                    name={'productSKU'}
                    value={Array.isArray(item.value) ? item.value.join(',') : item.value}
                    mlpVersion={MLPVersion.ONE}
                    label={t('admin.campaignConfig.productSKU')}
                    onChange={e => {
                      const inputValue = e.target.value;
                      const valuesArray = inputValue.split(',').map(item => item.trim());
                      handleOnChange(valuesArray, `activities.order.rules.${index}.${itemIndex}.value`);
                    }}
                    required={true}
                    status={
                      Array.isArray(item.value) && item.value.length > 0 && !item.value.some(v => v === '')
                        ? InputValidations.DEFAULT
                        : InputValidations.INVALID
                    }
                  />

                  <div className={styles['modal-order-rule-section']}>
                    <Button
                      type={ButtonTypes.BUTTON}
                      onClick={() => handleAdd(index, itemIndex)}
                      iconPosition={ButtonIconPosition.LEFT}
                      icon={{ iconName: 'plus' }}
                      size={ButtonSize.SMALL}
                      buttonStyle={ButtonStyles.OUTLINE}
                      className={styles['modal-order-rule-button']}
                      style={{ marginLeft: '20px' }}
                      data-testid="test-add-button-popup"
                    />
                  </div>

                  <div className={styles['modal-order-rule-section']}>
                    <Button
                      type={ButtonTypes.BUTTON}
                      onClick={e => {
                        handleDeleteButton(index, itemIndex);
                        e.stopPropagation();
                      }}
                      iconPosition={ButtonIconPosition.LEFT}
                      icon={{ iconName: 'minus', iconStyle: IconStyle.SOLID }}
                      size={ButtonSize.SMALL}
                      buttonStyle={ButtonStyles.OUTLINE}
                      className={styles['modal-order-rule-button']}
                      style={{ marginRight: '20px' }}
                      data-testid="test-add-button-popup"
                    />
                  </div>
                </div>
              ))}
          </Card>
        ))}
        <Button
          title={t('admin.campaignConfig.addRule')}
          type={ButtonTypes.BUTTON}
          onClick={() => handleAdd(formData?.activities?.order?.rules?.length)}
          iconPosition={ButtonIconPosition.LEFT}
          icon={{ iconName: 'plus' }}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.OUTLINE}
          data-testid="test-add-button-popup"
        />
      </div>
    </Modal>
  );
};
